import { compact } from 'lodash/fp';

import constants from '../../ahm-constants';

export type SitemapLink = {
  label: string;
  to: string;
  enabled?: boolean;
};

export type SitemapSection = {
  links: SitemapLink[];
  slug: string;
  title: string;
  isSubSection?: boolean;
};

const getSections = (): SitemapSection[] =>
  compact([
    {
      links: [
        {
          label: 'Log in',
          to: constants.URLS.MEMBERS.LOGIN,
        },
        {
          label: 'Register',
          to: constants.URLS.MEMBERS.REGISTRATION,
        },
        {
          label: 'Make a claim',
          to: constants.URLS.MEMBERS.CLAIMS,
        },
        {
          label: 'Claims history',
          to: constants.URLS.MEMBERS.CLAIMS_HISTORY,
        },
        {
          label: 'Make a payment',
          to: constants.URLS.MEMBERS.LOGGED_OUT_PAYMENTS,
        },
        {
          label: 'Change payment details',
          to: constants.URLS.MEMBERS.PAYMENT_DETAILS,
        },
        {
          label: 'Download your tax statement',
          to: constants.URLS.MEMBERS.DOCUMENTS,
        },
        {
          label: 'Update your details',
          to: constants.URLS.MEMBERS.SETTINGS,
        },
        {
          label: 'Request a new card',
          to: constants.URLS.MEMBERS.REPLACEMENT_CARD,
        },
      ],
      slug: 'members',
      title: 'Health insurance members',
    },
    {
      isSubSection: true,
      links: [
        {
          label: 'Find a provider',
          to: constants.URLS.MEMBERS.FIND_A_PROVIDER,
        },
        {
          label: 'Benefit calculator',
          to: constants.URLS.MEMBERS.CLAIMS_CALCULATOR,
        },
      ],
      slug: 'tools',
      title: 'Tools',
    },
    {
      links: [
        {
          label: 'Help',
          to: constants.URLS.HELP.HOME,
        },
        {
          label: 'Forms and guides',
          to: constants.URLS.SALES.DOWNLOADS,
        },
        {
          label: 'Contact us',
          to: constants.URLS.SALES.CONTACT_US,
        },
        {
          label: 'About ahm',
          to: constants.URLS.SALES.ABOUT,
        },
        {
          label: 'No gap dental check-ups',
          to: constants.URLS.SALES.NO_GAP_DENTAL,
        },
        {
          label: 'Tax and health insurance',
          to: constants.URLS.SALES.TAX,
        },
        {
          label: 'Going to hospital',
          to: constants.URLS.HELP.GOING_TO_HOSPITAL,
        },
        {
          label: 'Premium changes',
          to: constants.URLS.SALES.PREMIUM_CHANGE,
        },
        {
          label: 'For providers',
          to: constants.URLS.HELP.PROVIDERS,
        },
      ],
      slug: 'help-and-information',
      title: 'Help and information',
    },
    {
      links: [
        {
          label: 'ahm life insurance',
          to: constants.URLS.SALES.LIFE_INSURANCE,
        },
        {
          label: 'Get a quote',
          to: constants.URLS.THIRD_PARTY.LIFE_INSURANCE_QUOTE,
        },
        {
          label: 'Make a claim',
          to: constants.URLS.SALES.LIFE_INSURANCE_CLAIM,
        },
        {
          label: 'Contact us',
          to: constants.URLS.SALES.LIFE_INSURANCE_HELP,
        },
      ],
      slug: 'life-insurance',
      title: 'Life insurance',
    },
    {
      links: [
        {
          label: 'ahm income protection',
          to: constants.URLS.SALES.INCOME_INSURANCE,
        },
        {
          label: 'Get a quote',
          to: constants.URLS.THIRD_PARTY.INCOME_INSURANCE_QUOTE,
        },
        {
          label: 'Contact us',
          to: constants.URLS.SALES.LIFE_INSURANCE_CONTACT_US,
        },
        {
          label: 'Make a claim',
          to: constants.URLS.SALES.LIFE_INSURANCE_CLAIM,
        },
        {
          label: 'Forms and guides',
          to: constants.URLS.SALES.LIFE_INSURANCE_USEFUL_DOCUMENTS,
        },
      ],
      slug: 'income-protection',
      title: 'Income protection',
    },
    {
      links: [
        {
          label: 'Get a quote',
          to: constants.URLS.SALES.TRAVEL_INSURANCE,
        },
        {
          label: 'Make a claim',
          to: constants.URLS.SALES.TRAVEL_INSURANCE_MAKE_A_CLAIM,
        },
        {
          label: 'Travel warnings and alerts',
          to: constants.URLS.THIRD_PARTY.TRAVEL_INSURANCE_WARNINGS,
        },
        {
          label: 'Contact us',
          to: constants.URLS.SALES.TRAVEL_INSURANCE_CONTACT_US,
        },
      ],
      slug: 'travel-insurance',
      title: 'Travel insurance',
    },
    {
      links: [
        {
          label: 'Car insurance',
          to: constants.URLS.SALES.CAR_INSURANCE,
        },
        {
          label: 'Login',
          to: constants.URLS.SALES.CAR_INSURANCE_LOGIN,
        },
      ],
      slug: 'car-insurance',
      title: 'Car insurance',
    },
    {
      links: [
        {
          label: 'Home insurance',
          to: constants.URLS.SALES.HOME_INSURANCE,
        },
        {
          label: 'Login',
          to: constants.URLS.SALES.HOME_INSURANCE_LOGIN,
        },
      ],
      slug: 'home-insurance',
      title: 'Home insurance',
    },
    {
      links: compact([
        {
          label: 'Privacy policy',
          to: constants.URLS.SALES.PRIVACY,
        },
        {
          label: 'Terms and conditions',
          to: constants.URLS.SALES.TERMS_AND_CONDITIONS,
        },
        {
          label: 'Car and Home insurance privacy policy',
          to: constants.URLS.SALES.CAR_AND_HOME_INSURANCE_PRIVACY_POLICY,
        },
        {
          label: 'Security',
          to: constants.URLS.SALES.SECURITY_POLICY,
        },
        {
          label: 'Direct Debit Request Service Agreement',
          to: constants.URLS.SALES.DIRECT_DEBIT_SERVICE_AGREEMENT,
        },
        {
          label: 'Messaging privacy policy',
          to: constants.URLS.SALES.MESSAGING_PRIVACY_POLICY,
        },
      ]),
      slug: 'terms-and-policies',
      title: 'Terms and policies',
    },
    {
      links: [
        {
          label: 'Government reforms',
          to: constants.URLS.HELP.GOVERNMENT_REFORMS,
        },
        {
          label: 'Reforms 101',
          to: constants.URLS.HELP.PHI_REFORM_101,
        },
        {
          label: 'Natural therapies',
          to: constants.URLS.SALES.EXPLAINED_NATURAL_THERAPIES,
        },
        {
          label: 'Youth Discount',
          to: constants.URLS.SALES.YOUTH_DISCOUNT,
        },
        {
          label: '$750 excess',
          to: constants.URLS.HELP.EXCESS_750_OPTION,
        },
        {
          label: 'Clinical categories',
          to: constants.URLS.HELP.NEW_PRODUCT_CATEGORIES,
        },
      ],
      slug: 'government-reforms',
      title: 'Government reforms',
    },
  ]);

const getPerks = () => [
  {
    label: 'All member perks',
    to: constants.URLS.SALES.OFFERS_HOME,
  },
];

export default {
  getPerks,
  getSections,
};
